import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Callout,
    Chip,
    DataTable,
    EmptyState,
    SectionCard,
    SkeletonTable,
    type ColumnConfig,
} from '@/components/ui';
import { CreateAppRoleModal } from '../appRoles/CreateAppRoleModal';
import type { AppRoleTargetApp } from '../appRoles/useCreateAppRoleForm';
import { EditAppRoleDrawer } from './EditAppRoleDrawer';
import type { AppDetailPageVM } from './useAppDetailPage';
import type { AppRoleConversion } from './types';

type ConversionFilter = 'all' | 'converted' | 'unconverted';

/**
 * EAA-1817 (frame 47): Vai trò tab — the app's role catalog with each row's
 * conversion status to a standard role.
 *
 * "Người có quyền" (frame's 4th stat tile / per-row user count) is omitted:
 * no endpoint counts accounts by app role (same gap EAA-1819 already
 * documented for the registry's approval-consequence copy). "Việc làm được"
 * is rendered from `permissions.length` rather than fabricated prose — the
 * model has no free-text role description field.
 */
export function AppDetailRolesTab({ vm }: { vm: AppDetailPageVM }) {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState<ConversionFilter>('all');
    const [detailRole, setDetailRole] = useState<AppRoleConversion | null>(null);
    const [roleTargetApp, setRoleTargetApp] = useState<AppRoleTargetApp | null>(null);

    const matchesQuery = useMemo(() => {
        const q = search.trim().toLowerCase();
        return (r: AppRoleConversion) =>
            !q || r.key.toLowerCase().includes(q) || r.name.toLowerCase().includes(q);
    }, [search]);

    // EAA-1817: `include_inactive=true` (already fetched — see
    // `useAppDetailPage`) returns roles the mark-and-sweep catalog sync
    // deactivated. The 'all' filter now surfaces them (struck through, same
    // treatment `AdminAppRolesView`'s own docstring describes for frame 39) —
    // they were previously fetched and silently discarded by a hard
    // `isActive` filter here. Excluded from the converted/unconverted PILLS:
    // a removed role converts to nothing meaningful, so it would only ever
    // land in "unconverted" for the wrong reason.
    const visible = useMemo(() => {
        const active = vm.roleConversions
            .filter((r) => r.isActive)
            .filter(matchesQuery)
            .filter((r) => {
                if (filter === 'converted') return r.convertedTo.length > 0;
                if (filter === 'unconverted') return r.convertedTo.length === 0;
                return true;
            });
        if (filter !== 'all') return active;
        const removed = vm.roleConversions.filter((r) => !r.isActive).filter(matchesQuery);
        return [...active, ...removed];
    }, [vm.roleConversions, matchesQuery, filter]);

    // EAA-1884: hand-rolled `<table className="app-detail-roles-table">` →
    // kit `DataTable`, same conversion `AppsTable`/`AppDetailAuditTab`
    // already went through for this ticket family. No sort/filter existed on
    // the raw table (search + the all/converted/unconverted pills above are
    // page-local state, not column filters), so this stays exactly that —
    // `visible` still does the filtering, `DataTable` only renders it.
    const columns: ColumnConfig<AppRoleConversion>[] = useMemo(
        () => [
            {
                key: 'key',
                title: t('console.appDetail.roles.columnKey'),
                dataIndex: 'key',
                render: (value) => <code>{value as string}</code>,
            },
            {
                key: 'name',
                title: t('console.appDetail.roles.columnName'),
                dataIndex: 'name',
                render: (value) => (value as string) || '—',
            },
            {
                key: 'permissionCount',
                title: t('console.appDetail.roles.columnPermissions'),
                dataIndex: 'permissionCount',
                render: (value) =>
                    t('console.appDetail.roles.permissionCount', { count: value as number }),
            },
            {
                key: 'convertedTo',
                title: t('console.appDetail.roles.columnConvertedTo'),
                render: (_value, row) => (
                    <div className="app-detail-chip-wrap">
                        {!row.isActive ? (
                            <Chip tone="unmapped">—</Chip>
                        ) : row.convertedTo.length > 0 ? (
                            row.convertedTo.map((roleKey) => <Chip key={roleKey}>{roleKey}</Chip>)
                        ) : (
                            <Chip tone="unmapped">
                                {t('console.appDetail.roles.unconvertedChip')}
                            </Chip>
                        )}
                    </div>
                ),
            },
            {
                key: 'source',
                title: t('console.appDetail.roles.columnSource'),
                render: (_value, row) => (
                    // EAA-1884: a swept row (mark-and-sweep sync no longer
                    // reported it) is neither "synced" nor "manual" — it is
                    // gone from the app's own catalog, same struck-through
                    // treatment the design's frame 39 uses for this state.
                    <span
                        className={
                            'app-detail-source-pill' +
                            (!row.isActive
                                ? ' is-removed'
                                : row.isSynced
                                  ? ' is-synced'
                                  : ' is-manual')
                        }
                    >
                        {!row.isActive
                            ? t('console.appDetail.roles.sourceRemoved')
                            : row.isSynced
                              ? t('console.appDetail.roles.sourceSynced')
                              : t('console.appDetail.roles.sourceManual')}
                    </span>
                ),
            },
        ],
        [t]
    );

    const app = vm.app;
    if (!app) return null;

    return (
        <div className="app-detail-roles-tab">
            <div className="app-detail-stat-row">
                <div className="app-detail-stat-tile">
                    <span className="app-detail-stat-tile__label">
                        {t('console.appDetail.roles.statAppRoles')}
                    </span>
                    <span className="app-detail-stat-tile__value">{app.catalog_role_count}</span>
                </div>
                <div className="app-detail-stat-tile">
                    <span className="app-detail-stat-tile__label">
                        {t('console.appDetail.roles.statConverted')}
                    </span>
                    <span className="app-detail-stat-tile__value">{vm.convertedCount}</span>
                </div>
                <div className="app-detail-stat-tile">
                    <span className="app-detail-stat-tile__label">
                        {t('console.appDetail.roles.statUnconverted')}
                    </span>
                    <span className="app-detail-stat-tile__value is-warning">
                        {vm.unconvertedCount}
                    </span>
                </div>
            </div>

            {vm.unconvertedCount > 0 && (
                <Callout tone="warning" label={t('console.appDetail.roles.unconvertedCalloutLabel', {
                    count: vm.unconvertedCount,
                })}>
                    <span>
                        {t('console.appDetail.roles.unconvertedCalloutBody', { appName: app.name })}
                    </span>
                    <Button
                        type="link"
                        size="small"
                        className="app-detail-link-button"
                        onClick={() => setFilter('unconverted')}
                    >
                        {t('console.appDetail.roles.unconvertedCalloutAction', {
                            count: vm.unconvertedCount,
                        })}
                    </Button>
                </Callout>
            )}

            <div className="app-detail-roles-toolbar">
                <label className="app-detail-search">
                    {/* EAA-1846: frame 47 line 598 draws a search icon inside this
                        box — matches the .portal-orgs-search icon markup already
                        established in OrganizationsPage.tsx. */}
                    <svg
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        aria-hidden="true"
                    >
                        <circle cx="11" cy="11" r="7" />
                        <path d="m20 20-3.5-3.5" strokeLinecap="round" />
                    </svg>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('console.appDetail.roles.searchPlaceholder')}
                        aria-label={t('console.appDetail.roles.searchPlaceholder')}
                    />
                </label>
                <div className="app-detail-filter-group" role="group">
                    {/* EAA-1884: left as raw `<button>` — this is a 3-way
                        segmented toggle (`.is-active` pill highlight), not a
                        plain action. Kit `Button`'s variants (primary/default/
                        link/…) have no "selected pill in a group" state, so
                        forcing it through `Button` would mean re-deriving that
                        chrome by hand anyway; no such conversion precedent
                        exists elsewhere in this console area. */}
                    {(['all', 'converted', 'unconverted'] as ConversionFilter[]).map((key) => (
                        <button
                            key={key}
                            type="button"
                            className={'app-detail-filter-pill' + (filter === key ? ' is-active' : '')}
                            onClick={() => setFilter(key)}
                        >
                            {t(`console.appDetail.roles.filter.${key}`)}
                        </button>
                    ))}
                </div>
                <span className="app-detail-count-label">
                    {t('console.appDetail.roles.showingCount', {
                        // EAA-1817: removed rows are additional context under the
                        // 'all' filter, not part of the active catalog this ratio
                        // describes — counting them in `shown` produced a
                        // "Hiển thị 4 / 3" that reads as a bug, not a fourth row.
                        shown: visible.filter((r) => r.isActive).length,
                        total: vm.roleConversions.filter((r) => r.isActive).length,
                    })}
                </span>
                {/* EAA-1817: absent (not disabled) when the app has not opted in —
                    same convention `AppsTable`'s row menu already established for
                    this exact gate: `supports_role_management` is fail-closed
                    server-side, so a visible-but-disabled button would only ever
                    surface an action whose sole outcome is a validation error. */}
                {app.supports_role_management && (
                    <Button onClick={() => setRoleTargetApp({ code: app.code, name: app.name })}>
                        {t('console.appDetail.roles.addRoleButton')}
                    </Button>
                )}
            </div>

            <SectionCard bodyPadded={false}>
                {vm.appRolesQuery.isLoading && <SkeletonTable rows={6} columns={5} />}
                {vm.appRolesQuery.isSuccess && visible.length === 0 && (
                    <EmptyState
                        title={t('console.appDetail.roles.emptyTitle')}
                        description={t('console.appDetail.roles.emptyDescription')}
                    />
                )}
                {vm.appRolesQuery.isSuccess && visible.length > 0 && (
                    <div className="portal-table-scroll">
                        <DataTable<AppRoleConversion>
                            className="app-detail-roles-table"
                            data={visible}
                            columns={columns}
                            rowKey="id"
                            pagination={false}
                            rowClassName={(row) => (row.isActive ? '' : 'is-removed')}
                            actions={(row) => (
                                <Button
                                    type="link"
                                    size="small"
                                    className="app-detail-link-button"
                                    onClick={() => setDetailRole(row)}
                                >
                                    {t('console.appDetail.roles.viewDetail')}
                                </Button>
                            )}
                        />
                    </div>
                )}
            </SectionCard>

            <EditAppRoleDrawer role={detailRole} onClose={() => setDetailRole(null)} />
            <CreateAppRoleModal app={roleTargetApp} onClose={() => setRoleTargetApp(null)} />
        </div>
    );
}

export default AppDetailRolesTab;
